import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Chip,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import {
    OrganizationField,
    SelectedQuestion,
    SelectedItemComment,
    DirectMapping,
    ReportCell,
    CellMapping,
} from '../types/reportBuilder.types';

interface DirectMappingStepProps {
    selectedFields: OrganizationField[];
    selectedQuestions: SelectedQuestion[];
    selectedItemComments: SelectedItemComment[];
    directMappings: DirectMapping[];
    onDirectMappingsChange: (mappings: DirectMapping[]) => void;
    onApplyDirectMappings: (newCells: ReportCell[], newMappings: CellMapping[]) => void;
}

const FIELD_LABELS: Record<OrganizationField, string> = {
    inspectedOrgId: 'ID de Organización',
    inspectedOrgName: 'Nombre de Finca',
    inspectedOrgLogo: 'Logo',
    inspectedOrgRegionName: 'Región',
    inspectedOrgCityName: 'Ciudad',
    inspectedOrgVeredaName: 'Vereda',
    inspectedOrgGroupsName: 'Grupos',
    inspectedUserId: 'ID de Usuario',
    inspectedUserIdNumber: 'Cédula',
    inspectedUserName: 'Nombre del Productor',
    inspectedUserPhones: 'Teléfonos',
};

const DirectMappingStep: React.FC<DirectMappingStepProps> = ({
    selectedFields,
    selectedQuestions,
    selectedItemComments,
    directMappings,
    onDirectMappingsChange,
    onApplyDirectMappings,
}) => {
    const [hasApplied, setHasApplied] = useState(false);

    const isSelected = (type: DirectMapping['type'], sourceId: string): boolean => {
        return directMappings.some(dm => dm.type === type && dm.sourceId === sourceId);
    };

    const handleToggle = (type: DirectMapping['type'], sourceId: string, label: string) => {
        if (isSelected(type, sourceId)) {
            onDirectMappingsChange(
                directMappings.filter(dm => !(dm.type === type && dm.sourceId === sourceId))
            );
        } else {
            onDirectMappingsChange([...directMappings, { type, sourceId, label }]);
        }
        setHasApplied(false); // Reset applied state when selection changes
    };

    const handleApplyMappings = () => {
        const newCells: ReportCell[] = [];
        const newMappings: CellMapping[] = [];

        directMappings.forEach((dm, index) => {
            const cellId = uuidv4();

            // Create cell
            newCells.push({
                id: cellId,
                name: dm.label,
                order: index, // Will be adjusted by the parent based on existing cells
            });

            // Create mapping with questionType if it's a question
            const mapping: CellMapping = {
                cellId,
                sourceType: dm.type,
                sourceId: dm.sourceId,
            };

            // Add questionType for question mappings
            if (dm.type === 'question') {
                const sq = selectedQuestions.find(
                    q => `${q.programVersionItemId}:${q.question.uuid}` === dm.sourceId
                );
                if (sq) {
                    mapping.questionType = sq.question.type;
                }
            }

            newMappings.push(mapping);
        });

        onApplyDirectMappings(newCells, newMappings);
        setHasApplied(true);
    };

    const totalSelections = directMappings.length;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Mapeo Directo (Opcional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Selecciona los elementos que deseas mapear directamente. Para estos elementos, se crearán celdas y mapeos automáticamente sin necesidad de configuración adicional.
            </Typography>

            {totalSelections > 0 && (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        label={`${totalSelections} seleccionado${totalSelections > 1 ? 's' : ''}`}
                        color="primary"
                        variant="outlined"
                    />
                    <Button
                        variant="contained"
                        onClick={handleApplyMappings}
                        disabled={hasApplied}
                    >
                        {hasApplied ? '✓ Aplicado' : 'Aplicar Mapeo Directo'}
                    </Button>
                    {hasApplied && (
                        <Typography variant="body2" color="success.main">
                            Las celdas y mapeos se crearon correctamente. Puedes continuar al siguiente paso.
                        </Typography>
                    )}
                </Box>
            )}

            {/* Organization Fields */}
            {selectedFields.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Campos de Organización/Productor
                    </Typography>
                    <FormGroup>
                        {selectedFields.map(field => (
                            <FormControlLabel
                                key={field}
                                control={
                                    <Checkbox
                                        checked={isSelected('field', field)}
                                        onChange={() => handleToggle('field', field, FIELD_LABELS[field])}
                                        size="small"
                                    />
                                }
                                label={FIELD_LABELS[field]}
                            />
                        ))}
                    </FormGroup>
                </Paper>
            )}

            {/* Questions */}
            {selectedQuestions.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Preguntas del Formulario
                    </Typography>
                    <FormGroup>
                        {selectedQuestions.map(sq => {
                            // Use question.uuid instead of question.id to match FormAnswer.questionId
                            const sourceId = `${sq.programVersionItemId}:${sq.question.uuid}`;
                            const label = sq.question.text;
                            return (
                                <FormControlLabel
                                    key={sourceId}
                                    control={
                                        <Checkbox
                                            checked={isSelected('question', sourceId)}
                                            onChange={() => handleToggle('question', sourceId, label)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2">{sq.question.text}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {sq.programVersionItem.cpNumber}: {sq.programVersionItem.name}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            );
                        })}
                    </FormGroup>
                </Paper>
            )}

            {/* Item Comments */}
            {selectedItemComments.length > 0 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Comentarios de Items
                    </Typography>
                    <FormGroup>
                        {selectedItemComments.map(ic => {
                            const sourceId = ic.programVersionItemId;
                            const label = `Comentarios - ${ic.programVersionItem.cpNumber}: ${ic.programVersionItem.name}`;
                            return (
                                <FormControlLabel
                                    key={sourceId}
                                    control={
                                        <Checkbox
                                            checked={isSelected('itemComment', sourceId)}
                                            onChange={() => handleToggle('itemComment', sourceId, label)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography variant="body2">
                                                Comentarios del Item
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {ic.programVersionItem.cpNumber}: {ic.programVersionItem.name}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            );
                        })}
                    </FormGroup>
                </Paper>
            )}

            {selectedFields.length === 0 && selectedQuestions.length === 0 && selectedItemComments.length === 0 && (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        No hay elementos seleccionados. Regresa a los pasos anteriores para seleccionar campos, preguntas o comentarios.
                    </Typography>
                </Paper>
            )}

            {totalSelections === 0 && (selectedFields.length > 0 || selectedQuestions.length > 0 || selectedItemComments.length > 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    💡 Este paso es opcional. Puedes omitirlo y crear las celdas y mapeos manualmente en los siguientes pasos.
                </Typography>
            )}
        </Box>
    );
};

export default DirectMappingStep;
